import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralSelectMenu } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { PROFILE_REWARD__LIST } from 'src/constants/queryKeys';
import { networkCreateProfileReward, networkUpdateProfileReward } from 'src/network/profile-reward';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { schema } from './validationSchema';

const ProfileRewardForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();

  const { projectId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const { appInfo } = useAppInfoStore();

  const profileRewardDetails = modal?.data;
  const isEditMode = !!profileRewardDetails;

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const defaultValues = getDefaultValues(profileRewardDetails);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ProfileApplicationRewardApplicationID: +projectId,
      ProfileApplicationRewardTransactionAmountTypeID: selectedAmountType,
      ProfileApplicationRewardAmount: +values?.ProfileApplicationRewardAmount,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateProfileReward(profileRewardDetails?.ProfileApplicationRewardID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [PROFILE_REWARD__LIST] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateProfileReward(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [PROFILE_REWARD__LIST] });
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');
    setAmountTypes(amountTypeList);
    setSelectedAmountType(profileRewardDetails?.ProfileApplicationRewardTransactionAmountTypeID || amountTypeList?.[0]?.id);
  }, []);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <GeneralSelectMenu
                label="واحد پاداش"
                name="ProfileApplicationRewardTransactionAmountTypeID"
                options={amountTypes}
                value={selectedAmountType}
                onChangeValue={setSelectedAmountType}
              />

              <RHFTextField name="ProfileApplicationRewardAmount" type="text" label="مقدار پاداش" />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default ProfileRewardForm;
